import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import Breadcrumb from "../../components/common/Breadcrumb";
import NavigationTags from "../../components/common/NavigationTags";
import { generateTrackCreationFormBreadcrumbs } from "../../utils/breadcrumbHelpers";
import { CompanySingleTrackFolder } from "../../types/requestsOutputs";
import { getSingleTrackFolders, getSingleTrackFirstFilters } from "../../api/company";
import { getCategories } from "../../api/categories";
import UnifiedSingleTrackForm from "./UnifiedSingleTrackForm";
import hubStyles from "./Hub.module.css";
import pageHeader from "../../components/layouts/PageHeader.module.css";

const TrackCreationForm: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get("folderId");
    const categoryId = searchParams.get("categoryId");
    const hubId = searchParams.get("hubId");

    const [hub, setHub] = useState<CompanySingleTrackFolder | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [folderName, setFolderName] = useState<string>("");
    const [categoryName, setCategoryName] = useState<string>("");

    // Fetch hub and names on component mount
    useEffect(() => {
        if (hubId) {
            fetchHub();
        }
        fetchNames();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubId, categoryId, i18n.language]);

    const fetchNames = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            // Fetch hub name
            if (hubId) {
                const hubsResponse = await getSingleTrackFolders({
                    token,
                    data: { language: i18n.language || "en" },
                });
                const currentHub = hubsResponse.find((h) => h.id === hubId);
                if (currentHub) {
                    // Hub name will be set via the existing fetchHub function
                }
            }

            // Fetch folder name
            if (folderId && hubId) {
                const foldersResponse = await getSingleTrackFirstFilters({
                    token,
                    data: {
                        companySingleTrackFolderId: hubId,
                        language: i18n.language || "en",
                    },
                });
                const currentFolder = foldersResponse.find((f) => f.id === folderId);
                if (currentFolder) {
                    setFolderName(currentFolder.internalName);
                }
            }

            // Fetch category name
            if (categoryId && folderId) {
                const categoriesResponse = await getCategories({
                    token,
                    data: { firstFilterId: folderId },
                });
                const currentCategory = categoriesResponse.find((c) => c.id === categoryId);
                if (currentCategory) {
                    setCategoryName(currentCategory.name);
                }
            }
        } catch (error) {
            console.error("Error fetching names:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchHub = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token || !hubId) {
                return;
            }

            const response = await getSingleTrackFolders({
                token,
                data: { language: i18n.language || "en" },
            });
            const currentHub = response.find((h) => h.id === hubId);
            if (currentHub) {
                setHub(currentHub);
            }
        } catch (error) {
            console.error("Error fetching hub:", error);
            setErrorMessage(t("errorFetchingHub"));
        }
    };

    const handleFormSuccess = () => {
        // Navigate back to tracks list after successful creation
        navigate(`/hubs/folders/categories/tracks?hubId=${hubId}&folderId=${folderId}&categoryId=${categoryId}`);
    };

    if (loading) {
        return (
            <div className="form-loading-container">
                <div className={hubStyles.formLoadingSpinner}></div>
                <p className="form-loading-text">{t("loadingTrackCreation")}</p>
            </div>
        );
    }

    return (
        <div className={`${hubStyles.root} ${hubStyles.page}`}>
            {errorMessage && (
                <Alert severity="error" className="error-alert">
                    {errorMessage}
                </Alert>
            )}

            <div className={hubStyles.pageHeader}>
                <Breadcrumb
                    classes={{
                        nav: hubStyles.breadcrumbNav,
                        separator: hubStyles.breadcrumbSeparator,
                        link: hubStyles.breadcrumbLink,
                        text: hubStyles.breadcrumbText,
                        active: hubStyles.breadcrumbActive,
                    }}
                    items={generateTrackCreationFormBreadcrumbs({
                        hubId,
                        hubName: hub?.internalName || "",
                        folderId,
                        folderName,
                        categoryId,
                        categoryName,
                        t,
                    })}
                />
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className={hubStyles.settingsTitle}>{t("createNewTrack")}</h2>
                    </div>
                </div>
                <NavigationTags
                    classes={{
                        container: hubStyles.navigationTags,
                        tag: hubStyles.navigationTag,
                        tagActive: hubStyles.navigationTagActive,
                        tagDisabled: hubStyles.navigationTagDisabled,
                        arrow: hubStyles.navigationTagArrow,
                    }}
                    tags={[
                        { label: t("hub"), path: "/hubs" },
                        { label: t("page"), path: `/hubs/folders?hubId=${hubId}` },
                        { label: t("category"), path: `/hubs/folders/categories?folderId=${folderId}&hubId=${hubId}` },
                        {
                            label: t("content"),
                            path: `/hubs/folders/categories/tracks?hubId=${hubId}&folderId=${folderId}&categoryId=${categoryId}`,
                        },
                    ]}
                />
            </div>

            {/* Form Component */}
            <UnifiedSingleTrackForm
                setActiveTab={() => {}}
                folderId={folderId || undefined}
                categoryId={categoryId || undefined}
                hubId={hubId || undefined}
                hub={hub}
                onSuccess={handleFormSuccess}
            />
        </div>
    );
};

export default TrackCreationForm;
